import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { type AudioMaster, audioMaster } from '../audio/master';

/**
 * **O volume, na barra que já existe** (gate V28G.N.1).
 *
 * ## Porque aqui, e não num ecrã de opções
 *
 * Porque não há ecrã de opções. Este jogo não tinha uma única superfície de
 * configurações quando o primeiro som entrou - foi verificado antes de escolher
 * - e inventar uma tela inteira para dois controlos seria construir a casa por
 * causa da campainha.
 *
 * A barra superior já é onde vivem as coisas que pertencem à SESSÃO e não à
 * caça: a conta, o sair, o menu. O volume é dessa família.
 *
 * ## O que ele mostra sem ninguém carregar
 *
 * O estado, em duas linguagens ao mesmo tempo: **o desenho do altifalante** muda
 * quando está silenciado, e **a percentagem** está escrita ao lado. Nenhuma das
 * duas é cor - quem não distingue vermelho de cinzento lê na mesma, e quem tem o
 * ecrã a preto e branco também.
 *
 * ## Uma só subscrição
 *
 * `useSyncExternalStore` liga-se ao mestre e desliga-se com o componente. É o
 * que impede uma sessão longa - abrir o duelo, voltar à caça, abrir outra vez -
 * de deixar ouvintes pendurados no mestre.
 */

/** O desenho do altifalante. Traços, e não uma fonte com um emoji. */
function SpeakerGlyph({ muted, level }: { readonly muted: boolean; readonly level: number }) {
  return (
    <svg
      className="audio-glyph"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      width="16"
      height="16"
    >
      <title>Som</title>
      {/* O corpo, sempre igual: é o que faz o botão ser reconhecível. */}
      <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" />
      {muted ? (
        /*
         * A BARRA sobre o altifalante.
         *
         * É a forma que diz "silenciado" sem depender de cor nenhuma, e é a
         * mesma convenção que qualquer leitor de vídeo usa há trinta anos.
         */
        <path
          d="M16 9l6 6M22 9l-6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      ) : (
        <>
          {/* Uma onda a meio volume, duas acima dele: a forma também mede. */}
          <path
            d="M16 9.5a4 4 0 0 1 0 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
          {level > 0.5 ? (
            <path
              d="M19 7a8 8 0 0 1 0 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          ) : null}
        </>
      )}
    </svg>
  );
}

export function AudioControl({ master = audioMaster }: { readonly master?: AudioMaster }) {
  const settings = useSyncExternalStore(
    useCallback((listener: () => void) => master.subscribe(listener), [master]),
    useCallback(() => master.settings(), [master]),
    useCallback(() => master.settings(), [master]),
  );
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement | null>(null);

  /*
   * Fecha ao carregar fora, e ao carregar `Escape`.
   *
   * Registado só ENQUANTO está aberto, e removido no `cleanup`: um ouvinte de
   * documento que sobrevivesse ao painel seria a fuga que este gate proíbe.
   */
  useEffect(() => {
    if (!open) {
      return;
    }
    const away = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const percent = Math.round(settings.volume * 100);
  /* O que se OUVE, que é zero quando está silenciado por mais alto que esteja. */
  const shown = settings.muted ? 0 : percent;

  return (
    <div className="audio-control" ref={root}>
      <button
        type="button"
        className={`top-chip audio-toggle${settings.muted ? ' is-muted' : ''}`}
        data-testid="audio-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        /*
         * O nome diz o ESTADO, e não só a função.
         *
         * "Som" obrigaria quem não vê o desenho a abrir o painel para saber se
         * está ligado. O nome inteiro responde de uma vez.
         */
        aria-label={settings.muted ? 'Som silenciado. Abrir volume' : `Volume ${percent}%. Abrir`}
        onClick={() => setOpen((was) => !was)}
      >
        <SpeakerGlyph muted={settings.muted} level={settings.volume} />
        <span className="audio-level" data-testid="audio-level">
          {shown}%
        </span>
      </button>

      {open ? (
        <div className="audio-panel" id={panelId} data-testid="audio-panel">
          <label className="audio-slider-label" htmlFor={`${panelId}-range`}>
            Volume
          </label>
          <input
            id={`${panelId}-range`}
            className="audio-slider"
            data-testid="audio-volume"
            type="range"
            min={0}
            max={100}
            step={1}
            value={percent}
            aria-valuetext={`${percent}%`}
            onChange={(event) => master.setVolume(Number(event.target.value) / 100)}
          />
          <button
            type="button"
            className="audio-mute"
            data-testid="audio-mute"
            /*
             * `aria-pressed` e não um segundo rótulo: silenciar é um interruptor
             * com dois estados, e é assim que um leitor de ecrã o anuncia.
             */
            aria-pressed={settings.muted}
            onClick={() => master.toggleMuted()}
          >
            {settings.muted ? 'Ativar som' : 'Silenciar'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
